// src/utils/csvReport.js

import JSZip from "jszip";

function escapeCsvValue(value) {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function rowsToCsv(rows, columns) {
  const headers = columns.map((c) => escapeCsvValue(c.header ?? c.key)).join(",");
  const lines = rows.map((row) =>
    columns
      .map((c) => {
        const raw = row?.[c.key];
        const val = typeof c.format === "function" ? c.format(raw, row) : raw;
        return escapeCsvValue(val);
      })
      .join(",")
  );
  return [headers, ...lines].join("\r\n");
}

export function downloadCsv(csvText, filename = "flowtrak_export.csv") {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------------
   Date / age helpers
------------------------------ */
export function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toDateKey(d) {
  const dt = d instanceof Date ? d : safeDate(d);
  if (!dt) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function formatWhen(isoOrDate) {
  const d = isoOrDate instanceof Date ? isoOrDate : safeDate(isoOrDate);
  if (!d) return "";
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function pickNestedIso(ro, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = ro;
    for (const part of parts) cur = cur?.[part];
    if (cur) return cur;
  }
  return "";
}

function pickIso(ro, keys) {
  for (const k of keys) {
    const v = ro?.[k];
    if (v) return v;
  }
  return "";
}

function roCreatedISO(ro) {
  return (
    pickNestedIso(ro, [
      "timestamps.createdAt",
      "timestamps.openedAt",
      "timestamps.inAt",
      "timestamps.arrivedAt",
    ]) ||
    pickIso(ro, [
      "createdAtISO",
      "createdAtIso",
      "createdAtUtc",
      "createdAt",
      "openedAtISO",
      "openedAt",
      "inAt",
      "arrivedAt",
    ]) ||
    ""
  );
}

function roAssignedISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.assignedAt"]) ||
    pickIso(ro, ["assignedAtISO", "assignedAt"]) ||
    ""
  );
}

function roWorkStartISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.workStartAt"]) ||
    pickIso(ro, ["workStartAtISO", "workStartAt"]) ||
    ""
  );
}

function roWorkEndISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.workEndAt"]) ||
    pickIso(ro, ["workEndAtISO", "workEndAt"]) ||
    ""
  );
}

function roCompletedISO(ro) {
  return (
    pickNestedIso(ro, ["timestamps.completedAt"]) ||
    pickIso(ro, ["completedAtISO", "completedAt"]) ||
    ""
  );
}

function getAsOfDate(asOf) {
  return asOf instanceof Date && !Number.isNaN(asOf.getTime())
    ? asOf
    : safeDate(asOf) || new Date();
}

function ageMsFromCreated(ro, asOf = new Date()) {
  const created = safeDate(roCreatedISO(ro));
  if (!created) return null;

  const end = getAsOfDate(asOf);
  const diff = end.getTime() - created.getTime();

  if (!Number.isFinite(diff)) return null;
  return Math.max(0, diff);
}

function formatAgeFromMs(ms) {
  if (ms == null) return "";

  const totalMinutes = Math.max(0, Math.round(ms / 60000));

  if (totalMinutes < 60) return `${totalMinutes}m`;

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    return remainingMinutes
      ? `${totalHours}h ${remainingMinutes}m`
      : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}

function ageDays(ro, asOf = new Date()) {
  const ms = ageMsFromCreated(ro, asOf);
  if (ms == null) return "";
  return (ms / 86400000).toFixed(1);
}

function ageLabel(ro, asOf = new Date()) {
  return formatAgeFromMs(ageMsFromCreated(ro, asOf));
}

function createdDateLabel(ro) {
  return formatWhen(roCreatedISO(ro));
}

/* ------------------------------
   Summary export
------------------------------ */
export function exportKpiRangeCsv({
  ros = [],
  fromDateKey,
  toDateKey,
  filenamePrefix = "flowtrak_kpi",
} = {}) {
  const asOf = new Date();
  const csv = buildKpiSummaryCsv({ ros, fromDateKey, toDateKey, asOf });
  const file = `${filenamePrefix}_${fromDateKey || "start"}_to_${
    toDateKey || "end"
  }.csv`;
  downloadCsv(csv, file);
}

function buildKpiSummaryCsv({ ros = [], fromDateKey, toDateKey, asOf = new Date() }) {
  const from = safeDate(fromDateKey);
  const to = safeDate(toDateKey);
  const fromMs = from
    ? new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
    : null;
  const toMs = to
    ? new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1).getTime()
    : null;

  const completedISO = (ro) => roCompletedISO(ro);

  const inRange = (ro) => {
    const created = safeDate(roCreatedISO(ro));
    const ms = created?.getTime();
    if (!ms) return false;
    if (fromMs !== null && ms < fromMs) return false;
    if (toMs !== null && ms >= toMs) return false;
    return true;
  };

  const subset = ros.filter(inRange);

  const summary = {
    report_generated_at: formatWhen(asOf),
    age_calculated_as_of: formatWhen(asOf),
    range_from: fromDateKey || "",
    range_to: toDateKey || "",
    total_ros_created: subset.length,
    open_ros_now: ros.filter((r) => !safeDate(completedISO(r))).length,
    completed_in_range: subset.filter((r) => !!safeDate(completedISO(r))).length,
    waiters_in_range: subset.filter(
      (r) => String(r?.transportType || r?.transport || "").toUpperCase() === "WAITER"
    ).length,
  };

  const columns = [
    { key: "report_generated_at", header: "Report Generated At" },
    { key: "age_calculated_as_of", header: "Age Calculated As Of" },
    { key: "range_from", header: "Range From (YYYY-MM-DD)" },
    { key: "range_to", header: "Range To (YYYY-MM-DD)" },
    { key: "total_ros_created", header: "Total ROs Created (Range)" },
    { key: "completed_in_range", header: "Completed ROs (Range)" },
    { key: "waiters_in_range", header: "Waiters (Range)" },
    { key: "open_ros_now", header: "Open ROs Right Now" },
  ];

  return rowsToCsv([summary], columns);
}

/* ------------------------------
   WIP export
------------------------------ */
export function exportWipCsv({
  ros = [],
  dateKey,
  filters = {},
  filenamePrefix = "flowtrak_wip",
} = {}) {
  const asOf = new Date();
  const dk = dateKey || toDateKey(asOf);
  const csv = buildWipCsv({ ros, filters, asOf });
  const file = `${filenamePrefix}_${dk}_filtered.csv`;
  downloadCsv(csv, file);
}

function buildWipCsv({ ros = [], filters = {}, asOf = new Date() }) {
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const search = norm(filters.search);
  const waiterOnly = !!filters.waiterOnly;
  const partsStatus = norm(filters.partsStatus);

  const isOpen = (ro) => !safeDate(roCompletedISO(ro));

  const matches = (ro) => {
    if (!isOpen(ro)) return false;

    if (waiterOnly) {
      const t = String(ro?.transportType || ro?.transport || "").toUpperCase();
      if (t !== "WAITER") return false;
    }

    if (partsStatus) {
      const ps = norm(ro?.partsStatus || ro?.parts || ro?.parts_state);
      if (ps !== partsStatus) return false;
    }

    if (search) {
      const hay = [
        ro?.roNumber,
        ro?.vin,
        ro?.customerName,
        ro?.advisorName,
        ro?.techName,
        ro?.technicianName,
        ro?.status,
        ro?.lifecycle,
        ro?.notes,
      ]
        .map((x) => norm(x))
        .join(" | ");
      if (!hay.includes(search)) return false;
    }

    return true;
  };

  const rows = ros
    .filter(matches)
    .map((ro) => ({
      roNumber: ro?.roNumber ?? ro?.ro ?? "",
      vin: ro?.vin ?? "",
      customerName: ro?.customerName ?? "",
      advisorName: ro?.advisorName ?? "",
      techName: ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? "",
      status: ro?.status ?? "",
      lifecycle: ro?.lifecycle ?? "",
      transportType: ro?.transportType ?? ro?.transport ?? "",
      partsStatus: ro?.partsStatus ?? "",
      promiseTime: formatWhen(ro?.promiseTimeISO || ro?.promiseTime || ro?.promiseAt),
      createdDate: createdDateLabel(ro),
      ageDays: ageDays(ro, asOf),
      age: ageLabel(ro, asOf),
      updatedAt: formatWhen(ro?.timestamps?.updatedAt || ro?.updatedAtISO || ro?.updatedAt),
      notes: ro?.notes ?? "",
    }))
    .sort((a, b) => Number(b.ageDays || 0) - Number(a.ageDays || 0));

  const columns = [
    { key: "roNumber", header: "RO Number" },
    { key: "vin", header: "VIN" },
    { key: "customerName", header: "Customer" },
    { key: "advisorName", header: "Advisor" },
    { key: "techName", header: "Technician" },
    { key: "status", header: "Status" },
    { key: "lifecycle", header: "Lifecycle" },
    { key: "transportType", header: "Transport (WAITER/LOANER/etc)" },
    { key: "partsStatus", header: "Parts Status" },
    { key: "promiseTime", header: "Promise Time" },
    { key: "createdDate", header: "Created Date" },
    { key: "ageDays", header: "Age Days" },
    { key: "age", header: "Age" },
    { key: "updatedAt", header: "Updated" },
    { key: "notes", header: "Notes" },
  ];

  return rowsToCsv(rows, columns);
}

/* ------------------------------
   KPI Detail + Tech/Parts rollups
------------------------------ */
function safeNumLocal(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function secondsBetweenISO(aIso, bIso) {
  const a = safeDate(aIso)?.getTime();
  const b = safeDate(bIso)?.getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.max(0, Math.floor((b - a) / 1000));
}

function inCreatedRange(ro, fromDateKey, toDateKey) {
  const from = safeDate(fromDateKey);
  const to = safeDate(toDateKey);
  const fromMs = from
    ? new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime()
    : null;
  const toMs = to
    ? new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1).getTime()
    : null;

  const created = safeDate(roCreatedISO(ro))?.getTime();
  if (!created) return false;
  if (fromMs !== null && created < fromMs) return false;
  if (toMs !== null && created >= toMs) return false;
  return true;
}

function computeRoMetrics(ro) {
  const created = roCreatedISO(ro);
  const assigned = roAssignedISO(ro);
  const workStart = roWorkStartISO(ro);
  const workEnd = roWorkEndISO(ro);
  const completed = roCompletedISO(ro);

  const secCreatedToAssigned = secondsBetweenISO(created, assigned);
  const secAssignedToCompleted = secondsBetweenISO(assigned, completed);
  const secWorked = secondsBetweenISO(workStart, workEnd);

  const partsReq = ro?.partsActionRequestedAt || "";
  const partsAct = ro?.partsActionedAt || "";
  const secPartsTurnaround = secondsBetweenISO(partsReq, partsAct);

  const flagged = safeNumLocal(ro?.flaggedHours);
  const workedHours = secWorked != null ? secWorked / 3600 : 0;
  const efficiency = workedHours > 0 ? flagged / workedHours : null;

  return {
    created,
    assigned,
    workStart,
    workEnd,
    completed,
    secCreatedToAssigned,
    secAssignedToCompleted,
    secWorked,
    secPartsTurnaround,
    flagged,
    workedHours,
    efficiency,
  };
}

function buildKpiDetailCsv({ ros = [], fromDateKey, toDateKey, asOf = new Date() }) {
  const subset = ros.filter((ro) => inCreatedRange(ro, fromDateKey, toDateKey));

  const rows = subset.map((ro) => {
    const m = computeRoMetrics(ro);
    const tech =
      ro?.techName ??
      ro?.technicianName ??
      ro?.assignedTechName ??
      ro?.assignedTechId ??
      "";

    return {
      roNumber: ro?.roNumber ?? ro?.ro ?? "",
      vin: ro?.vin ?? "",
      customerName: ro?.customerName ?? "",
      advisorName: ro?.advisorName ?? "",
      techName: tech,
      status: ro?.status ?? "",
      lifecycle: ro?.lifecycle ?? "",
      transportType: ro?.transportType ?? ro?.transport ?? "",
      partsStatus: ro?.partsStatus ?? "",
      promiseTime: formatWhen(ro?.promiseTimeISO || ro?.promiseTime || ro?.promiseAt),

      createdDate: formatWhen(m.created),
      ageDays: ageDays(ro, asOf),
      age: ageLabel(ro, asOf),
      assignedAt: formatWhen(m.assigned),
      completedAt: formatWhen(m.completed),

      dispatchToAssign_min:
        m.secCreatedToAssigned == null ? "" : (m.secCreatedToAssigned / 60).toFixed(1),
      assignToComplete_min:
        m.secAssignedToCompleted == null ? "" : (m.secAssignedToCompleted / 60).toFixed(1),

      workStartAt: formatWhen(m.workStart),
      workEndAt: formatWhen(m.workEnd),
      techWorked_hrs: m.secWorked == null ? "" : m.workedHours.toFixed(2),

      flaggedHours: m.flagged.toFixed(1),
      techEfficiency_flagged_per_worked:
        m.efficiency == null ? "" : m.efficiency.toFixed(2),

      partsRequestedAt: formatWhen(ro?.partsActionRequestedAt),
      partsActionedAt: formatWhen(ro?.partsActionedAt),
      partsTurnaround_min:
        m.secPartsTurnaround == null ? "" : (m.secPartsTurnaround / 60).toFixed(1),

      notes: ro?.notes ?? "",
    };
  });

  const columns = [
    { key: "roNumber", header: "RO Number" },
    { key: "vin", header: "VIN" },
    { key: "customerName", header: "Customer" },
    { key: "advisorName", header: "Advisor" },
    { key: "techName", header: "Technician" },
    { key: "status", header: "Status" },
    { key: "lifecycle", header: "Lifecycle" },
    { key: "transportType", header: "Transport" },
    { key: "partsStatus", header: "Parts Status" },
    { key: "promiseTime", header: "Promise Time" },
    { key: "createdDate", header: "Created Date" },
    { key: "ageDays", header: "Age Days" },
    { key: "age", header: "Age" },
    { key: "assignedAt", header: "Assigned At" },
    { key: "completedAt", header: "Completed At" },
    { key: "dispatchToAssign_min", header: "Dispatch→Assign (min)" },
    { key: "assignToComplete_min", header: "Assign→Complete (min)" },
    { key: "workStartAt", header: "Work Start" },
    { key: "workEndAt", header: "Work End" },
    { key: "techWorked_hrs", header: "Tech Worked (hrs)" },
    { key: "flaggedHours", header: "Flagged Hours" },
    {
      key: "techEfficiency_flagged_per_worked",
      header: "Tech Efficiency (Flagged/Worked)",
    },
    { key: "partsRequestedAt", header: "Parts Requested At" },
    { key: "partsActionedAt", header: "Parts Actioned At" },
    { key: "partsTurnaround_min", header: "Parts Turnaround (min)" },
    { key: "notes", header: "Notes" },
  ];

  return rowsToCsv(rows, columns);
}

function buildTechEfficiencyCsv({ ros = [], fromDateKey, toDateKey }) {
  const subset = ros
    .filter((ro) => inCreatedRange(ro, fromDateKey, toDateKey))
    .filter((ro) => !!safeDate(roCompletedISO(ro)));

  const byTech = new Map();

  subset.forEach((ro) => {
    const tech =
      ro?.techName ??
      ro?.technicianName ??
      ro?.assignedTechName ??
      ro?.assignedTechId ??
      "Unassigned";
    const m = computeRoMetrics(ro);

    if (!byTech.has(tech)) {
      byTech.set(tech, {
        techName: tech,
        completedCount: 0,
        totalFlagged: 0,
        totalWorked: 0,
      });
    }

    const t = byTech.get(tech);
    t.completedCount += 1;
    t.totalFlagged += safeNumLocal(m.flagged);
    t.totalWorked += safeNumLocal(m.workedHours);
  });

  const rows = Array.from(byTech.values())
    .map((t) => {
      const eff = t.totalWorked > 0 ? t.totalFlagged / t.totalWorked : null;
      return {
        techName: t.techName,
        completedCount: t.completedCount,
        totalFlaggedHours: t.totalFlagged.toFixed(1),
        totalWorkedHours: t.totalWorked.toFixed(2),
        efficiency_flagged_per_worked: eff == null ? "" : eff.toFixed(2),
      };
    })
    .sort(
      (a, b) =>
        Number(b.efficiency_flagged_per_worked || 0) -
        Number(a.efficiency_flagged_per_worked || 0)
    );

  const columns = [
    { key: "techName", header: "Technician" },
    { key: "completedCount", header: "Completed ROs" },
    { key: "totalFlaggedHours", header: "Total Flagged Hours" },
    { key: "totalWorkedHours", header: "Total Worked Hours" },
    { key: "efficiency_flagged_per_worked", header: "Efficiency (Flagged/Worked)" },
  ];

  return rowsToCsv(rows, columns);
}

function buildPartsTimingCsv({ ros = [], fromDateKey, toDateKey, asOf = new Date() }) {
  const subset = ros.filter((ro) => inCreatedRange(ro, fromDateKey, toDateKey));

  const rows = subset
    .filter((ro) => ro?.partsActionRequestedAt || ro?.partsActionedAt)
    .map((ro) => {
      const m = computeRoMetrics(ro);
      return {
        roNumber: ro?.roNumber ?? "",
        advisorName: ro?.advisorName ?? "",
        techName: ro?.techName ?? ro?.technicianName ?? ro?.assignedTechName ?? "",
        partsStatus: ro?.partsStatus ?? "",
        createdDate: createdDateLabel(ro),
        ageDays: ageDays(ro, asOf),
        age: ageLabel(ro, asOf),
        partsRequestedAt: formatWhen(ro?.partsActionRequestedAt),
        partsActionedAt: formatWhen(ro?.partsActionedAt),
        partsTurnaround_min:
          m.secPartsTurnaround == null ? "" : (m.secPartsTurnaround / 60).toFixed(1),
      };
    });

  const columns = [
    { key: "roNumber", header: "RO Number" },
    { key: "advisorName", header: "Advisor" },
    { key: "techName", header: "Technician" },
    { key: "partsStatus", header: "Parts Status" },
    { key: "createdDate", header: "Created Date" },
    { key: "ageDays", header: "Age Days" },
    { key: "age", header: "Age" },
    { key: "partsRequestedAt", header: "Parts Requested At" },
    { key: "partsActionedAt", header: "Parts Actioned At" },
    { key: "partsTurnaround_min", header: "Parts Turnaround (min)" },
  ];

  return rowsToCsv(rows, columns);
}

export async function exportManagerReportZip({
  ros = [],
  fromDateKey,
  toDateKey,
  filenamePrefix = "flowtrak_manager_report",
}) {
  const zip = new JSZip();
  const asOf = new Date();

  const safeFrom = fromDateKey || "start";
  const safeTo = toDateKey || "end";

  const summaryCsv = buildKpiSummaryCsv({ ros, fromDateKey, toDateKey, asOf });
  const detailCsv = buildKpiDetailCsv({ ros, fromDateKey, toDateKey, asOf });
  const techCsv = buildTechEfficiencyCsv({ ros, fromDateKey, toDateKey });
  const partsCsv = buildPartsTimingCsv({ ros, fromDateKey, toDateKey, asOf });

  zip.file(`01_summary_${safeFrom}_to_${safeTo}.csv`, summaryCsv);
  zip.file(`02_ro_detail_${safeFrom}_to_${safeTo}.csv`, detailCsv);
  zip.file(`03_tech_efficiency_${safeFrom}_to_${safeTo}.csv`, techCsv);
  zip.file(`04_parts_timing_${safeFrom}_to_${safeTo}.csv`, partsCsv);

  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `${filenamePrefix}_${safeFrom}_to_${safeTo}.zip`);
}